<template>
  <div class="card mb-4 supplier-contracts">
    <div class="card-header">
      <h5 class="card-title mb-3">
        <i class="fas fa-file-contract me-2"></i>
        Contratos
      </h5>
      <div class="date-selector">
        <div class="row g-2">
          <div class="col-md-6">
            <label class="form-label small mb-1">Data Inicial:</label>
            <input 
              type="date" 
              v-model="startDate"
              @change="onDateChange"
              class="form-control form-control-sm"
            />
          </div>
          <div class="col-md-6">
            <label class="form-label small mb-1">Data Final:</label>
            <input 
              type="date" 
              v-model="endDate"
              @change="onDateChange"
              class="form-control form-control-sm"
            />
          </div>
        </div>
        <small class="text-muted mt-1 d-block">
          <i class="fas fa-info-circle me-1"></i>
          Período máximo: 5 anos
        </small>
      </div>
    </div>
    <div class="card-body">
      <div v-if="loading" class="text-center">
        <div class="spinner-border" role="status">
          <span class="visually-hidden">Carregando...</span>
        </div>
        <p class="mt-2">Carregando contratos...</p>
        <small class="text-muted" v-if="loadingWindows > 1">
          Consultando {{ loadingWindows }} períodos em paralelo
        </small>
      </div>
      
      <div v-else-if="error" class="alert alert-danger" role="alert">
        <i class="fas fa-exclamation-triangle me-2"></i>
        {{ error }}
      </div>
      
      <div v-else-if="contracts.length === 0" class="alert alert-info" role="alert">
        <i class="fas fa-info-circle me-2"></i>
        Nenhum contrato encontrado para este fornecedor no período consultado.
      </div>
      
      <div v-else>
        <!-- Informações do período e fonte -->
        <div class="alert alert-light border mb-3">
          <div class="row">
            <div class="col-md-6">
              <small class="text-muted">
                <i class="fas fa-calendar-alt me-1"></i>
                <strong>Período:</strong> {{ searchPeriod }}
              </small>
            </div>
            <div class="col-md-6">
              <small class="text-muted">
                <i class="fas fa-database me-1"></i>
                <strong>Fonte:</strong> Portal de Dados Abertos do Governo Federal
              </small>
            </div>
          </div>
        </div>
        
        <!-- Resumo -->
        <div class="row mb-3">
          <div class="col-md-6">
            <div class="d-flex align-items-center">
              <i class="fas fa-file-alt me-2 text-primary"></i>
              <div>
                <strong>Total de Contratos:</strong>
                <span class="badge bg-primary ms-2">{{ contracts.length }}</span>
              </div>
            </div>
          </div>
          <div class="col-md-6">
            <div class="d-flex align-items-center">
              <i class="fas fa-dollar-sign me-2 text-success"></i>
              <div>
                <strong>Valor Total:</strong>
                <span class="badge bg-success ms-2">{{ formatCurrency(totalValue) }}</span>
              </div>
            </div>
          </div>
        </div>
        
        <!-- Cards dos contratos -->
        <div class="contracts-list">
          <div v-for="contract in contracts" :key="contract.numeroContrato" class="contract-card mb-3">
            <div class="card">
              <div class="card-body">
                <div class="d-flex justify-content-between align-items-start mb-2">
                  <h6 class="card-title mb-0">
                    <i class="fas fa-file-contract me-2 text-primary"></i>
                    {{ contract.numeroContrato }}
                  </h6>
                  <span class="badge bg-success">{{ formatCurrency(contract.valorGlobal) }}</span>
                </div>
                
                <p class="card-text mb-2">
                  <small class="text-muted">{{ contract.nomeModalidadeCompra }}</small>
                </p>
                
                <div class="mb-2">
                  <strong class="text-dark">{{ contract.nomeOrgao }}</strong>
                  <br>
                  <small class="text-muted">{{ contract.nomeUnidadeGestora }}</small>
                </div>
                
                <div class="mb-2">
                  <strong>Objeto:</strong>
                  <p class="mb-1">{{ contract.objeto }}</p>
                  <small class="text-muted">
                    <i class="fas fa-tag me-1"></i>
                    {{ contract.nomeCategoria }}
                  </small>
                </div>
                
                <div class="row">
                  <div class="col-6">
                    <small class="text-muted">Início:</small>
                    <br>
                    <strong>{{ formatDate(contract.dataVigenciaInicial) }}</strong>
                  </div>
                  <div class="col-6">
                    <small class="text-muted">Fim:</small>
                    <br>
                    <strong>{{ formatDate(contract.dataVigenciaFinal) }}</strong>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
export default {
  name: 'SupplierContracts',
  props: {
    supplierDocument: {
      type: String,
      required: true
    },
    periodMonths: {
      type: Number,
      default: 12
    }
  },
  data() {
    return {
      contracts: [],
      loading: false,
      error: null,
      startDate: this.getDefaultStartDate(),
      endDate: this.getDefaultEndDate(),
      loadingWindows: 0
    }
  },
  computed: {
    totalValue() {
      return this.contracts.reduce((total, contract) => total + (contract.valorGlobal || 0), 0)
    },
    searchPeriod() {
      if (!this.startDate || !this.endDate) return 'Período não definido'
      
      const start = new Date(this.startDate)
      const end = new Date(this.endDate)
      
      return `${start.toLocaleDateString('pt-BR')} até ${end.toLocaleDateString('pt-BR')}`
    }
  },
  mounted() {
    this.fetchContracts()
  },
  methods: {
    async fetchContracts() {
      this.loading = true
      this.error = null
      
      try {
        if (!this.startDate || !this.endDate) {
          this.error = 'Por favor, selecione as datas de início e fim'
          this.loading = false
          return
        }

        // Generate 1-year windows
        const dateWindows = this.generateDateWindows(this.startDate, this.endDate)
        this.loadingWindows = dateWindows.length
        
        // Make parallel requests for each window
        const promises = dateWindows.map(window => 
          fetch(`/catalog/supplier_contracts?document=${this.supplierDocument}&start_date=${window.start}&end_date=${window.end}`)
            .then(response => {
              if (!response.ok) {
                throw new Error(`Erro na requisição: ${response.status}`)
              }
              return response.json()
            })
            .then(data => data.resultado || [])
            .catch(err => {
              console.error(`Erro na janela ${window.start} - ${window.end}:`, err)
              return [] // Return empty array for failed requests
            })
        )
        
        // Wait for all requests to complete
        const results = await Promise.all(promises)
        
        // Merge all results and remove duplicates
        const allContracts = results.flat()
        const uniqueContracts = this.removeDuplicateContracts(allContracts)
        
        this.contracts = uniqueContracts
      } catch (err) {
        this.error = 'Erro ao carregar contratos: ' + err.message
      } finally {
        this.loading = false
      }
    },

    removeDuplicateContracts(contracts) {
      const seen = new Set()
      return contracts.filter(contract => {
        const key = `${contract.numeroContrato}-${contract.codigoOrgao}-${contract.dataVigenciaInicial}`
        if (seen.has(key)) {
          return false
        }
        seen.add(key)
        return true
      })
    },
    
    onDateChange() {
      if (this.startDate && this.endDate) {
        if (this.validateDateRange()) {
          this.fetchContracts()
        }
      }
    },
    
    validateDateRange() {
      const start = new Date(this.startDate)
      const end = new Date(this.endDate)
      
      // Check if start date is after end date
      if (start > end) {
        this.error = 'A data inicial não pode ser posterior à data final'
        return false
      }
      
      // Check if period is more than 5 years
      const diffInMs = end.getTime() - start.getTime()
      const diffInYears = diffInMs / (1000 * 60 * 60 * 24 * 365.25)
      
      if (diffInYears > 5) {
        this.error = 'O período entre as datas não pode ser maior que 5 anos'
        return false
      }
      
      // Clear error if validation passes
      this.error = null
      return true
    },

    generateDateWindows(startDate, endDate) {
      const windows = []
      let currentStart = new Date(startDate)
      const finalEnd = new Date(endDate)
      
      while (currentStart < finalEnd) {
        const currentEnd = new Date(currentStart)
        currentEnd.setFullYear(currentEnd.getFullYear() + 1)
        
        // If current end goes beyond final end, use final end
        if (currentEnd > finalEnd) {
          currentEnd.setTime(finalEnd.getTime())
        }
        
        windows.push({
          start: currentStart.toISOString().split('T')[0],
          end: currentEnd.toISOString().split('T')[0]
        })
        
        // Move to next year
        currentStart = new Date(currentEnd)
        currentStart.setDate(currentStart.getDate() + 1)
      }
      
      return windows
    },
    
    getDefaultStartDate() {
      const date = new Date()
      date.setMonth(date.getMonth() - this.periodMonths)
      return date.toISOString().split('T')[0]
    },
    
    getDefaultEndDate() {
      const date = new Date()
      return date.toISOString().split('T')[0]
    },
    
    formatCurrency(value) {
      if (!value) return 'R$ 0,00'
      return new Intl.NumberFormat('pt-BR', {
        style: 'currency',
        currency: 'BRL'
      }).format(value) // Value is already in reais from the API
    },
    
    formatDate(dateString) {
      if (!dateString) return '-'
      const date = new Date(dateString)
      return date.toLocaleDateString('pt-BR')
    }
  }
}
</script>